// A map showing a team or a match (teams and matches, Ben 2026-10-30): a team's shots or passes in a
// competition + season, its own ("for") or its opponents' ("against") (/team-shots, /team-passes),
// or its passes in one match (/team-passes), each with its player.

import type { ProtoEvent, TeamPassesResponse, TeamShotsResponse } from "@catbento/shared";
import type { LiveApi } from "./api.js";
import { toProtoEvent } from "./event-map.js";
import { rememberFamiliarName } from "../data/familiar-names.js";
import { getPlayerShortName } from "../data/player-utils.js";

export type ShotSide = "for" | "against";

export async function loadTeamShots(api: LiveApi, teamId: number, competitionId: number, seasonCode: string, side: ShotSide): Promise<ProtoEvent[]> {
  const { shots } = await api.get<TeamShotsResponse>(`/team-shots?teamId=${teamId}&competitionId=${competitionId}&seasonId=${seasonCode}&side=${side}`);
  return shots.map((s) => {
    rememberFamiliarName(s.playerName, s.playerNickname);
    const e = toProtoEvent(s);
    return s.playerId !== null && s.playerName !== null ? { ...e, player: { id: s.playerId, name: s.playerName } } : e;
  });
}

/** One team's passes in one match, each with its passer (a Pass Map showing a match). */
export async function loadTeamMatchPasses(api: LiveApi, teamId: number, matchId: number): Promise<ProtoEvent[]> {
  const { passes } = await api.get<TeamPassesResponse>(`/team-passes?teamId=${teamId}&matchId=${matchId}`);
  return passes.map(withPasser);
}

/** A team's passes in a competition + season (Ben, 2026-11-02), its own or its opponents' in its
 *  games, each with its passer (a Pass Map showing a team). The server sends them slim + zipped. */
export async function loadTeamSeasonPasses(api: LiveApi, teamId: number, competitionId: number, seasonCode: string, side: ShotSide): Promise<ProtoEvent[]> {
  const { passes } = await api.get<TeamPassesResponse>(`/team-passes?teamId=${teamId}&competitionId=${competitionId}&seasonId=${seasonCode}&side=${side}`);
  return passes.map(withPasser);
}

function withPasser(p: TeamPassesResponse["passes"][number]): ProtoEvent {
  rememberFamiliarName(p.playerName, p.playerNickname);
  const e = toProtoEvent(p);
  return p.playerId !== null && p.playerName !== null ? { ...e, player: { id: p.playerId, name: p.playerName } } : e;
}

export interface Shooter {
  id: number;
  /** As the site shows names (the familiar one, else first + last). */
  name: string;
  shots: number;
  goals: number;
}

/** The players in a set of events (shooters, passers), most first (then most goals, then A–Z).
 *  Goals count only shots that went in. Pure. */
export function shootersOf(shots: readonly ProtoEvent[]): Shooter[] {
  const by = new Map<number, Shooter>();
  for (const s of shots) {
    if (!s.player) continue;
    let row = by.get(s.player.id);
    if (!row) {
      row = { id: s.player.id, name: getPlayerShortName({ name: s.player.name }), shots: 0, goals: 0 };
      by.set(s.player.id, row);
    }
    row.shots += 1;
    const outcome = (s.raw_event_attributes as { shot?: { outcome?: { name?: string } } }).shot?.outcome?.name;
    if (outcome === "Goal") row.goals += 1;
  }
  return [...by.values()].sort((a, b) => b.shots - a.shots || b.goals - a.goals || a.name.localeCompare(b.name));
}
